import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Menu, FileDown, ShoppingBag, ArrowRight, Truck, ClipboardList, Users, Warehouse, Bell } from "lucide-react";

function Counter({ value, color }) {
  if (value === null || value === undefined) {
    return <span className="text-[10px] text-zinc-600 font-mono animate-pulse">...</span>;
  }
  return (
    <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${color}`}>
      {value}
    </span>
  );
}

export default function AgentDashboard() {
  const { authAxios, user } = useAuth();
  // Live counters on action cards
  const [kpi, setKpi] = useState({
    giri_fermate: null,
    giri_completate: null,
    orders_total: null,
    orders_da_trasferire: null,
    clients: null,
  });

  useEffect(() => {
    const fetchKpi = async () => {
      try {
        const [giriRes, ordersRes, oStatsRes, cliRes] = await Promise.all([
          authAxios.get("/giri"),
          authAxios.get("/orders"),
          authAxios.get("/orders/stats"),
          authAxios.get("/clients"),
        ]);
        const giri = Array.isArray(giriRes.data) ? giriRes.data : [];
        const fermate = giri.reduce((acc, g) => acc + (g.fermate || 0), 0);
        const completate = giri.reduce((acc, g) => acc + (g.completate || 0), 0);
        const ordersList = Array.isArray(ordersRes.data) ? ordersRes.data : [];
        setKpi({
          giri_fermate: fermate,
          giri_completate: completate,
          orders_total: ordersList.length,
          orders_da_trasferire: oStatsRes.data?.da_trasferire ?? 0,
          clients: Array.isArray(cliRes.data) ? cliRes.data.length : 0,
        });
      } catch (e) { console.error("Failed to load agent KPI:", e); }
    };
    fetchKpi();
  }, [authAxios]);

  return (
    <div className="min-h-screen bg-[#09090b] flex" data-testid="agent-dashboard">
      <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-5xl">
        <PageHeader title="Dashboard" />
        <div className="flex items-center gap-3 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
              Ciao, {user?.name || "Agente"}
            </h1>
            <p className="text-sm text-zinc-500">Pannello Agente · Triolo.Store</p>
          </div>
        </div>

        {/* Quick Actions */}
        <h2 className="text-lg font-semibold text-white mb-4">Azioni Rapide</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/agent/giri" data-testid="go-giri-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-sky-500/40 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-sky-500/10">
                    <Truck className="w-6 h-6 text-sky-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium">Giri</p>
                      {kpi.giri_fermate !== null && kpi.giri_fermate > 0 && (
                        <Counter
                          value={`${kpi.giri_completate}/${kpi.giri_fermate}`}
                          color="bg-sky-500/20 text-sky-300"
                        />
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">Giri di presa ordini giornalieri</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-sky-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/agent/orders" data-testid="go-orders-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-violet-500/40 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-violet-500/10">
                    <ClipboardList className="w-6 h-6 text-violet-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium">Ordini</p>
                      {kpi.orders_total !== null && (
                        <Counter value={kpi.orders_total} color="bg-violet-500/20 text-violet-300" />
                      )}
                      {kpi.orders_da_trasferire !== null && kpi.orders_da_trasferire > 0 && (
                        <Counter
                          value={`${kpi.orders_da_trasferire} da trasf.`}
                          color="bg-amber-500/20 text-amber-300"
                        />
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">I miei ordini · da trasferire</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-violet-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/agent/clients" data-testid="go-clients-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-cyan-500/40 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-cyan-500/10">
                    <Users className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium">Clienti</p>
                      {kpi.clients !== null && (
                        <Counter value={kpi.clients} color="bg-cyan-500/20 text-cyan-300" />
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">Anagrafica clienti assegnati</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-cyan-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/catalog" data-testid="go-catalog-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-amber-500/30 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-amber-500/10">
                    <ShoppingBag className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Catalogo</p>
                    <p className="text-xs text-zinc-500">Sfoglia il catalogo prodotti</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-amber-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/exports" data-testid="go-exports-btn">
            <Card className="bg-[#18181b] border-white/5 hover:border-emerald-500/30 transition-colors cursor-pointer group">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-emerald-500/10">
                    <FileDown className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Esportazioni PDF</p>
                    <p className="text-xs text-zinc-500">Prezzi personalizzati, promo, catalogo</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-emerald-400 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          {user?.push_enabled && (
            <Link to="/admin/push-status" data-testid="go-notifications-btn">
              <Card className="bg-[#18181b] border-white/5 hover:border-amber-500/30 transition-colors cursor-pointer group">
                <CardContent className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-amber-500/10">
                      <Bell className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Notifiche</p>
                      <p className="text-xs text-zinc-500">Scegli quali notifiche ricevere</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-amber-400 transition-colors" />
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Sezione Magazzino: solo per agenti con poteri di magazziniere */}
          {user?.magazziniere_extra && (
            <Link to="/warehouse" data-testid="go-warehouse-btn">
              <Card className="bg-[#18181b] border-white/5 hover:border-fuchsia-500/40 transition-colors cursor-pointer group">
                <CardContent className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-fuchsia-500/10">
                      <Warehouse className="w-6 h-6 text-fuchsia-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium flex items-center gap-2">
                        Magazzino
                        <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-300">
                          EXTRA
                        </span>
                      </p>
                      <p className="text-xs text-zinc-500">Giri consegne · Picking · Ricevuti</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-fuchsia-400 transition-colors" />
                </CardContent>
              </Card>
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}
